import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { A2pRegistration, PhoneNumber } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { Badge, Button, Card, EmptyState, Field, Input, Skeleton } from '../ui/index.js';
import { useToast } from '../ui/toast.js';

interface AvailableNumber { e164: string; locality?: string; region?: string; tollFree: boolean }
interface MessagingStatus {
  registration: A2pRegistration | null;
  numbers: PhoneNumber[];
  canText: boolean;
  registrarAvailable: boolean;
}

export function Settings() {
  return (
    <>
      <div className="page-head"><div><div className="page-title">Settings</div><div className="page-sub">Business configuration</div></div></div>
      <Texting />
    </>
  );
}

/** Self-service texting: provision a number in one click, then register for A2P 10DLC to unlock SMS. */
function Texting() {
  const toast = useToast();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['messaging'], queryFn: () => api.get<MessagingStatus>('/comms/messaging') });
  const refetch = () => qc.invalidateQueries({ queryKey: ['messaging'] });
  const err = (e: unknown, fallback: string) => toast.push(e instanceof ApiRequestError ? e.message : fallback, 'error');

  if (status.isLoading) return <Card style={{ maxWidth: 760 }}><div className="card-pad"><Skeleton h={120} /></div></Card>;
  const data = status.data;
  const number = data?.numbers[0];
  const reg = data?.registration;

  return (
    <Card style={{ maxWidth: 760 }}>
      <div className="card-head">
        <span className="card-title">Texting</span>
        {data?.canText
          ? <Badge tone="success">Active — you can text customers</Badge>
          : <Badge tone="warning">Setup required</Badge>}
      </div>
      <div className="card-pad stack gap-4">
        <StepNumber number={number} onDone={refetch} onError={err} />
        {number && <StepA2p number={number} reg={reg ?? null} canText={!!data?.canText} onDone={refetch} onError={err} />}
        <p className="subtle" style={{ fontSize: 'var(--text-xs)' }}>
          Numbers are provisioned and registered through <strong>Telnyx</strong> — searched, ordered, and A2P-registered
          entirely from Worksy (customers never touch a carrier portal). Dev uses a fake provider (no charges).
        </p>
      </div>
    </Card>
  );
}

/** Step 1 — provision a business number (one click, or pick from a search). */
function StepNumber({ number, onDone, onError }: { number?: PhoneNumber; onDone: () => void; onError: (e: unknown, f: string) => void }) {
  const toast = useToast();
  const [areaCode, setAreaCode] = useState('512');
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const quickSetup = async () => {
    setBusy('quick');
    try { await api.post('/comms/setup', { areaCode }); toast.push('Number provisioned', 'success'); onDone(); }
    catch (e) { onError(e, 'Setup failed'); } finally { setBusy(null); }
  };
  const search = async () => {
    setBusy('search');
    try { setResults((await api.get<{ items: AvailableNumber[] }>(`/comms/numbers/search?areaCode=${encodeURIComponent(areaCode)}`)).items); }
    catch (e) { onError(e, 'Search failed'); } finally { setBusy(null); }
  };
  const provision = async (e164: string) => {
    setBusy(e164);
    try { await api.post('/comms/numbers', { e164, label: 'Main line' }); toast.push(`Provisioned ${e164}`, 'success'); setResults(null); onDone(); }
    catch (e) { onError(e, 'Provision failed'); } finally { setBusy(null); }
  };

  if (number) {
    return (
      <div>
        <StepLabel n={1} title="Business number" done />
        <div className="row between" style={{ padding: '6px 0' }}>
          <div className="row gap-2"><strong className="tabular">{number.e164}</strong>{number.label && <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{number.label}</span>}</div>
          <Badge tone={number.a2pStatus === 'approved' ? 'success' : 'warning'}>
            {number.a2pStatus === 'approved' ? 'Ready to text' : number.a2pStatus === 'not_registered' ? 'Not registered' : number.a2pStatus}
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="stack gap-2">
      <StepLabel n={1} title="Get a business number" />
      <div className="row gap-2" style={{ alignItems: 'flex-end' }}>
        <Field label="Area code"><Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} style={{ width: 110 }} inputMode="numeric" /></Field>
        <Button loading={busy === 'quick'} onClick={quickSetup}>Get a number</Button>
        <Button variant="ghost" loading={busy === 'search'} onClick={search}>Choose from a list</Button>
      </div>
      {results && (results.length === 0 ? <EmptyState icon="☏" title="No numbers found" />
        : <div className="stack gap-2">{results.map((n) => (
            <div key={n.e164} className="row between" style={{ padding: '6px 0', borderTop: '1px solid var(--color-border)' }}>
              <div><strong className="tabular">{n.e164}</strong> <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{n.locality}, {n.region}</span></div>
              <Button size="sm" loading={busy === n.e164} onClick={() => provision(n.e164)}>Provision</Button>
            </div>
          ))}</div>)}
    </div>
  );
}

/** Step 2 — A2P 10DLC registration: brand (+sole-prop OTP) → campaign → active. */
function StepA2p({ number, reg, canText, onDone, onError }: {
  number: PhoneNumber; reg: A2pRegistration | null; canText: boolean; onDone: () => void; onError: (e: unknown, f: string) => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [brandType, setBrandType] = useState<'sole_proprietor' | 'standard'>('sole_proprietor');
  const [form, setForm] = useState<Record<string, string>>({ country: 'US', vertical: 'PROFESSIONAL' });
  const [pin, setPin] = useState('');
  const set = (k: string) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (canText) {
    return (
      <div>
        <StepLabel n={2} title="Carrier registration (A2P 10DLC)" done />
        <p className="subtle" style={{ fontSize: 'var(--text-sm)' }}>
          {reg?.business.displayName} is registered and approved. Your texts send with full carrier deliverability.
        </p>
      </div>
    );
  }

  const register = async () => {
    setBusy(true);
    try { await api.post('/comms/a2p/register', { ...form, brandType }); toast.push('Registration submitted', 'success'); onDone(); }
    catch (e) { onError(e, 'Registration failed'); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post('/comms/a2p/verify-otp', { pin }); toast.push('Verified — texting is live!', 'success'); setPin(''); onDone(); }
    catch (e) { onError(e, 'Verification failed'); } finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try { await api.post('/comms/a2p/refresh', {}); onDone(); }
    catch (e) { onError(e, 'Refresh failed'); } finally { setBusy(false); }
  };

  // Sole proprietor awaiting OTP entry.
  if (reg?.status === 'otp_sent') {
    return (
      <div className="stack gap-2">
        <StepLabel n={2} title="Verify your mobile number" />
        <p className="subtle" style={{ fontSize: 'var(--text-sm)' }}>We texted a code to {reg.business.mobilePhone}. Enter it to finish and activate texting.</p>
        <div className="row gap-2" style={{ alignItems: 'flex-end' }}>
          <Field label="Verification code"><Input value={pin} onChange={(e) => setPin(e.target.value)} style={{ width: 140 }} inputMode="numeric" /></Field>
          <Button loading={busy} onClick={verify}>Verify &amp; activate</Button>
        </div>
      </div>
    );
  }

  // Registration in flight (brand pending / campaign pending): poll for status.
  if (reg && reg.status !== 'not_started' && reg.status !== 'failed') {
    return (
      <div className="stack gap-2">
        <StepLabel n={2} title="Carrier registration in progress" />
        <div className="row gap-2">
          <Badge tone="warning">{prettyStatus(reg.status)}</Badge>
          <Button size="sm" variant="ghost" loading={busy} onClick={refresh}>Check status</Button>
        </div>
        <p className="subtle" style={{ fontSize: 'var(--text-xs)' }}>
          Brand: {reg.brandStatus ?? '—'} · Campaign: {reg.campaignStatus ?? '—'}. Standard brands can take a few minutes to hours; we’ll flip to Active automatically.
        </p>
      </div>
    );
  }

  // Not started — show the registration form.
  return (
    <div className="stack gap-3">
      <StepLabel n={2} title="Register to text (A2P 10DLC)" />
      <p className="subtle" style={{ fontSize: 'var(--text-sm)' }}>
        Carriers require every business to register before texting consumers. Sole proprietor is fastest (no EIN, verified by a text to your phone).
      </p>
      <div className="row gap-2">
        <Button size="sm" variant={brandType === 'sole_proprietor' ? 'primary' : 'ghost'} onClick={() => setBrandType('sole_proprietor')}>Sole proprietor</Button>
        <Button size="sm" variant={brandType === 'standard' ? 'primary' : 'ghost'} onClick={() => setBrandType('standard')}>Registered business (EIN)</Button>
      </div>
      <div className="stack gap-2">
        <Field label="Business display name"><Input value={form.displayName ?? ''} onChange={set('displayName')} placeholder="Austin Roofing Co" /></Field>
        {brandType === 'sole_proprietor' ? (
          <div className="row gap-2">
            <Field label="First name"><Input value={form.firstName ?? ''} onChange={set('firstName')} /></Field>
            <Field label="Last name"><Input value={form.lastName ?? ''} onChange={set('lastName')} /></Field>
            <Field label="Mobile (for code)"><Input value={form.mobilePhone ?? ''} onChange={set('mobilePhone')} placeholder="+15125551212" /></Field>
          </div>
        ) : (
          <div className="row gap-2">
            <Field label="Legal business name"><Input value={form.legalName ?? ''} onChange={set('legalName')} /></Field>
            <Field label="EIN / Tax ID"><Input value={form.ein ?? ''} onChange={set('ein')} placeholder="12-3456789" /></Field>
          </div>
        )}
        <div className="row gap-2">
          <Field label="Email"><Input value={form.email ?? ''} onChange={set('email')} /></Field>
          <Field label="Business phone"><Input value={form.phone ?? ''} onChange={set('phone')} placeholder="+15125550000" /></Field>
        </div>
        <Field label="Street"><Input value={form.street ?? ''} onChange={set('street')} /></Field>
        <div className="row gap-2">
          <Field label="City"><Input value={form.city ?? ''} onChange={set('city')} /></Field>
          <Field label="State"><Input value={form.state ?? ''} onChange={set('state')} style={{ width: 80 }} /></Field>
          <Field label="ZIP"><Input value={form.postalCode ?? ''} onChange={set('postalCode')} style={{ width: 110 }} /></Field>
        </div>
      </div>
      <div><Button loading={busy} onClick={register}>Submit registration</Button></div>
    </div>
  );
}

function StepLabel({ n, title, done }: { n: number; title: string; done?: boolean }) {
  return (
    <div className="row gap-2" style={{ alignItems: 'center' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%',
        fontSize: 'var(--text-xs)', fontWeight: 600, background: done ? 'var(--color-success)' : 'var(--color-border)',
        color: done ? '#fff' : 'var(--color-text)',
      }}>{done ? '✓' : n}</span>
      <strong style={{ fontSize: 'var(--text-sm)' }}>{title}</strong>
    </div>
  );
}

function prettyStatus(s: A2pRegistration['status']): string {
  return ({
    not_started: 'Not started', brand_pending: 'Brand under review', otp_sent: 'Awaiting verification code',
    brand_verified: 'Brand verified', campaign_pending: 'Campaign under review', active: 'Active', failed: 'Needs attention',
  } as const)[s] ?? s;
}
